/**
 * RIDAA Theme Core JavaScript
 * Handles Navigation, Header scroll states, Accordions, Accessibility & Focus Traps
 */

class RidaaTheme {
  constructor() {
    this.initHeader();
    this.initMobileNav();
    this.initAccordions();
    this.initAnnouncementBar();
    this.initLocalization();
    this.initAccessibility();
    this.initShopifyDesignMode();
  }

  initHeader() {
    const headerWrapper = document.querySelector('.header-wrapper');
    if (!headerWrapper) return;

    let lastScrollY = window.scrollY;
    const isSticky = headerWrapper.classList.contains('header-wrapper--sticky');

    window.addEventListener('scroll', () => {
      const currentScrollY = window.scrollY;

      if (isSticky) {
        if (currentScrollY > 120) {
          headerWrapper.classList.add('is-scrolled');
          if (currentScrollY > lastScrollY && currentScrollY > 250) {
            headerWrapper.style.transform = 'translateY(-100%)';
          } else {
            headerWrapper.style.transform = 'translateY(0)';
          }
        } else {
          headerWrapper.classList.remove('is-scrolled');
          headerWrapper.style.transform = 'translateY(0)';
        }
      }
      lastScrollY = currentScrollY;
    }, { passive: true });
  }

  initMobileNav() {
    const toggleBtn = document.querySelector('[data-mobile-nav-toggle]');
    const drawer = document.querySelector('[data-mobile-nav-drawer]');
    const closeBtn = document.querySelector('[data-mobile-nav-close]');
    const backdrop = document.querySelector('[data-mobile-nav-backdrop]');

    if (!toggleBtn || !drawer) return;

    const getFocusables = () => {
      return Array.from(drawer.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'))
        .filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
    };

    const openDrawer = () => {
      drawer.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      drawer.setAttribute('aria-hidden', 'false');
      toggleBtn.setAttribute('aria-expanded', 'true');

      const focusables = getFocusables();
      if (focusables.length > 0) {
        setTimeout(() => focusables[0].focus(), 50);
      }
    };

    const closeDrawer = () => {
      drawer.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-open');
      document.body.style.overflow = '';
      drawer.setAttribute('aria-hidden', 'true');
      toggleBtn.setAttribute('aria-expanded', 'false');
      toggleBtn.focus();
    };

    toggleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const isOpen = drawer.classList.contains('is-open');
      if (isOpen) {
        closeDrawer();
      } else {
        openDrawer();
      }
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        closeDrawer();
      });
    }

    if (backdrop) {
      backdrop.addEventListener('click', (e) => {
        e.preventDefault();
        closeDrawer();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (!drawer.classList.contains('is-open')) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        closeDrawer();
        return;
      }

      if (e.key === 'Tab') {
        const focusables = getFocusables();
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  initAccordions() {
    document.querySelectorAll('.accordion-header').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.closest('.accordion-item');
        const isOpen = item.classList.contains('is-open');

        // Toggle state
        if (isOpen) {
          item.classList.remove('is-open');
          btn.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  initAnnouncementBar() {
    const bar = document.querySelector('.announcement-bar');
    if (!bar) return;
    // Any rotation logic if multiple announcements exist
  }

  initLocalization() {
    document.querySelectorAll('[data-localization-select]').forEach((select) => {
      select.addEventListener('change', (e) => {
        const form = select.closest('form');
        if (form) form.submit();
      });
    });
  }

  initAccessibility() {
    // Focus visible indicator helper
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        document.body.classList.add('user-is-tabbing');
      }
    });
  }

  initShopifyDesignMode() {
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', (e) => {
        if (e.target && (e.target.querySelector('.header') || e.target.classList.contains('shopify-section-group-header-group'))) {
          this.initHeader();
          this.initMobileNav();
        }
        if (e.target && e.target.querySelector('.accordion-header')) {
          this.initAccordions();
        }
      });
    }
  }
}

// Global initialization
document.addEventListener('DOMContentLoaded', () => {
  window.Ridaa = window.Ridaa || {};
  window.Ridaa.theme = new RidaaTheme();
});
